import { AnalysisRun, AnalysisRunService } from "../services/analysis-run.service";
import { ANALYSIS_TIMEOUTS, AnalysisError, bounded } from "../utils/analysis-async";
import { EvaluationFlowsEnum } from "../models/shared.model";
import { Inject, Injector } from "@angular/core";
import { MailUtils } from "../utils/mail.utils";
import {
  IEvaluationStepsConfig,
  LogLine,
  Node,
  NodeData,
  OverviewTextReplacements,
  ResultInfo
} from "../models/shared.model";
import { Flow } from "../models/flow";
import { InitFlow } from "../evaluator-flows/initFlow";
import { RiskLevel } from "../models/risk-level";
import { StepResult } from "../models/step-result";
import { StringUtils } from "../utils/string.utils";
import { EvaluatorResult } from "../models/evaluator-result";
import { IEvaluator } from "../interfaces/evaluator-interface";
import { NodeResultDecisions } from "../models/constants";
import { WaitService } from "../services/wait.service";
import { DecodedQRCode } from "../models/qr-code.model";


export class Evaluator implements IEvaluator {

  readonly run: AnalysisRun;
  private waitService: WaitService;
  private flowData: NodeData;
  private evaluationFlow = new Flow();

  logArr: LogLine[] = [];

  constructor(
    @Inject(Injector) private injector: Injector,
    private type: string,
    private flowConfig: IEvaluationStepsConfig,
    private attachment?: Office.AttachmentDetails,
    private link?: HTMLAnchorElement,
    private linkStyle?: CSSStyleDeclaration,
    private qrCode?: DecodedQRCode,
  ) {

    this.run = this.injector.get(AnalysisRunService).current;
    this.run.assertActive();
    // Init the flow nodes
    this.evaluationFlow = new InitFlow(this.injector, this.flowConfig).getFlow();
    this.waitService = this.injector.get(WaitService);


  }

  getType(): string {
    return this.type;
  }

  getFlow(): Flow {
    return this.evaluationFlow;
  }

  async runAnalysis(): Promise<EvaluatorResult> {
    this.run.assertActive();
      if (!this.evaluationFlow) throw new AnalysisError('unavailable', 'Evaluation flow missing');


      const senderEmail = this.run.item.sender.emailAddress.toLowerCase();

      let domain = MailUtils.parseMultiLevelDomain(senderEmail);

      this.flowData = {
        senderEmail: senderEmail,
        domain: domain,
        topLevelDomain: MailUtils.getTopLevelDomain(domain, true),
        score: 0
      };

      let resultInfo: ResultInfo = {
        sender: senderEmail,
        domain: domain,
        topLevelDomain: this.flowData.topLevelDomain,
        error: []
      };

      if (this.attachment) {
        this.flowData.attachment = this.attachment;
        resultInfo.attachment = this.attachment;
      }

      if (this.link) {
        this.flowData.link = this.link;
        resultInfo.link = this.link.getAttribute('href');
        resultInfo.linkStyle = this.linkStyle;
      }

      if (this.qrCode) {
        this.flowData.qrCode = this.qrCode;
        resultInfo.qrCode = this.qrCode;
      }

      // Get the starting node of flow
      let currentNode = this.evaluationFlow.getNode(this.flowConfig.startId);

      while (currentNode) {
        // The result of the process fn should match the available nodePaths keys
        let res: StepResult;
        try {
          this.run.assertActive();
          res = await bounded(() => currentNode.process(this.flowData, resultInfo),
            ANALYSIS_TIMEOUTS.office, currentNode.description || currentNode.id, this.run);
          this.run.assertActive();
          if (!res) throw new AnalysisError('failed', 'Evaluator returned no result');
          if (res.result === NodeResultDecisions.PENDING) {
            this.run.issue(this.type as EvaluationFlowsEnum,
              new AnalysisError('unavailable', 'Check is pending'), `node:${currentNode.logId}`);
          }

          let decision = res.result;
          resultInfo = res.info ?? resultInfo;

          if (!currentNode.nodePaths[decision])
            break;


          const resPoints = currentNode.nodePaths[decision].points;
          this.flowData.score += resPoints.value;

          let overview = this.setOverviewText(currentNode, decision, this.flowData);

          currentNode.result = {
            decision: decision,
            points: resPoints.value,
            overviewText: overview,
            info: resultInfo,
          };

          this.setLogs(currentNode, decision, resPoints.value, resultInfo, res.reasonLog);

          currentNode = this.evaluationFlow.getNode(currentNode.nodePaths[decision].nextNode);

        } catch (e) {
          this.run.assertActive();
          this.run.issue(this.type as EvaluationFlowsEnum, e, `node:${currentNode.logId}`);
          resultInfo.error.push(`Failed to ${currentNode.description}`);
          const pendingPath = currentNode.nodePaths[NodeResultDecisions.PENDING];
          const points = pendingPath?.points;
          if (!points || !Number.isFinite(points.value)) break;
          this.flowData.score += points.value;
          currentNode.result = {
            decision: NodeResultDecisions.PENDING,
            points: points.value
          };

          this.setLogs(currentNode, NodeResultDecisions.PENDING, points.value, resultInfo, `Failed to ${currentNode.description}`);

          currentNode = this.evaluationFlow.getNode(currentNode.nodePaths[NodeResultDecisions.PENDING]?.nextNode);
          if (!currentNode)
            break;
        }
      }

      this.run.assertActive();
      return new EvaluatorResult(this.flowConfig.name, RiskLevel.Moderate, this.flowData.score, "Description", resultInfo);
  }


  private setOverviewText(currentNode: Node, decision: any, flowData: NodeData) {
    const nodeOverviewText = currentNode?.nodePaths[decision].overviewText;

    if (!nodeOverviewText)
      return undefined;

    const replacements: OverviewTextReplacements = {
      tld: flowData.topLevelDomain
    };

    nodeOverviewText.value = StringUtils.replacePlaceholdersOverviewText(nodeOverviewText.value, replacements);

    return { ...nodeOverviewText, flow: this.type };
  }

  private setLogs(currentNode: Node, decision: any, points: number, resultInfo: ResultInfo, reason: string) {
    this.logArr.push(new LogLine(currentNode.logId, this.getType(), currentNode.id, decision, reason, points, this.link, resultInfo.displayedAs, this.attachment, this.qrCode));

  }

  getLogs(): LogLine[] {
    return this.logArr;
  }
}

